import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { X, Check } from 'lucide-react';
import type { LookupDto, CreateLookupRequest, UpdateLookupRequest } from '../types/lookup';

interface LookupFormProps {
  /** Lookup item being edited (null for create mode) */
  lookup?: LookupDto | null;
  /** Called when form is submitted successfully */
  onSubmit: (data: CreateLookupRequest | UpdateLookupRequest) => Promise<void>;
  /** Called when form is cancelled */
  onCancel: () => void;
  /** Whether form is currently submitting */
  isSubmitting?: boolean;
  /** Error message to display */
  error?: string | null;
  /** Whether to show in inline mode (compact) */
  inline?: boolean;
}

/**
 * Internal form component that receives initial values
 * This pattern avoids using useEffect to sync state
 */
function LookupFormInner({
  initialName,
  initialIsActive,
  isEditMode,
  onSubmit,
  onCancel,
  isSubmitting = false,
  error,
  inline = false,
}: {
  initialName: string;
  initialIsActive: boolean;
  isEditMode: boolean;
  onSubmit: (data: CreateLookupRequest | UpdateLookupRequest) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
  error?: string | null;
  inline?: boolean;
}) {
  const [name, setName] = useState(initialName);
  const [isActive, setIsActive] = useState(initialIsActive);
  const [validationError, setValidationError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');

    // Validation
    const trimmedName = name.trim();
    if (!trimmedName) {
      setValidationError('Name is required');
      return;
    }

    if (trimmedName.length > 100) {
      setValidationError('Name must be 100 characters or less');
      return;
    }

    try {
      if (isEditMode) {
        const updateData: UpdateLookupRequest = {
          name: trimmedName,
          isActive,
        };
        await onSubmit(updateData);
      } else {
        const createData: CreateLookupRequest = {
          name: trimmedName,
        };
        await onSubmit(createData);
      }
    } catch {
      // Error is handled by parent via error prop
    }
  };

  const displayError = validationError || error;

  if (inline) {
    return (
      <form onSubmit={handleSubmit} className="flex items-center gap-2">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter name"
          className="flex-1"
          disabled={isSubmitting}
          autoFocus
        />
        {isEditMode && (
          <div className="flex items-center gap-2">
            <Checkbox
              id="lookup-active-inline"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              disabled={isSubmitting}
              aria-label="Active status"
            />
            <Label htmlFor="lookup-active-inline" className="text-sm text-muted-foreground cursor-pointer">
              Active
            </Label>
          </div>
        )}
        <Button
          type="submit"
          size="icon"
          disabled={isSubmitting}
          title={isEditMode ? 'Save changes' : 'Add item'}
        >
          <Check className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={onCancel}
          disabled={isSubmitting}
          title="Cancel"
        >
          <X className="h-4 w-4" />
        </Button>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <ErrorAlert error={displayError} />

      <div className="space-y-2">
        <Label htmlFor="lookup-name">Name *</Label>
        <Input
          id="lookup-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter name"
          disabled={isSubmitting}
          autoFocus
        />
      </div>

      {isEditMode && (
        <div className="flex items-center gap-2">
          <Checkbox
            id="lookup-active"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            disabled={isSubmitting}
          />
          <Label htmlFor="lookup-active" className="font-normal cursor-pointer">
            Active
          </Label>
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : isEditMode ? 'Save Changes' : 'Add Item'}
        </Button>
      </div>
    </form>
  );
}

/**
 * Form for creating or editing a lookup item
 * Supports both inline (compact) and expanded modes
 * Uses key-based remounting to reset form state when lookup changes
 */
export function LookupForm({
  lookup,
  onSubmit,
  onCancel,
  isSubmitting = false,
  error,
  inline = false,
}: LookupFormProps) {
  const isEditMode = !!lookup;
  // Use lookup id as key to force remount when switching between items
  const formKey = lookup?.id ?? 'new';

  return (
    <LookupFormInner
      key={formKey}
      initialName={lookup?.name ?? ''}
      initialIsActive={lookup?.isActive ?? true}
      isEditMode={isEditMode}
      onSubmit={onSubmit}
      onCancel={onCancel}
      isSubmitting={isSubmitting}
      error={error}
      inline={inline}
    />
  );
}
